import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';

import {
  TriggerPoint,
  TriggerPointInfo,
  TriggerPointsListResponse,
  TriggerPointResponse,
  TriggerPointRecommendationsResponse,
  RecommendationItemType,
  RecommendationRequestParams,
  RecommendationsContext,
  ConnectorPredictions
} from './trigger-point-recommendations.model';

import appConfig from 'src/config/config';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ArticleService } from 'src/app/services/article/article.service';
import { Article, ArticleType, ArticlesSuccessResponse } from 'src/app/services/yeti-protocol/article';
import { ArticleTagsService } from 'src/app/services/article/article-tags.service';
import { ItemType } from 'src/app/services/yeti-protocol/utils/enums';

class TriggerPointRecommendationsServiceConfig {
  recommendationsUrl: string;
}

@Injectable()
export class TriggerPointRecommendationsService {

  config: TriggerPointRecommendationsServiceConfig = appConfig;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    public articleService: ArticleService,
    public articleTagsService: ArticleTagsService,
  ) { }

  getTriggerPoints(): Promise<Array<TriggerPoint>> {
    const serverUrl = `${this.config.recommendationsUrl}/trigger-points`;
    return firstValueFrom(this.http.get<TriggerPointsListResponse>(serverUrl,).pipe(
      map((res) => {
        return res.triggerPoints;
      })
    ));
  }

  getTriggerPoint(triggerPointId: string): Promise<TriggerPointInfo> {
    const serverUrl = `${this.config.recommendationsUrl}/trigger-point/${triggerPointId}`;
    return firstValueFrom(this.http.get<TriggerPointResponse>(serverUrl,).pipe(
      map((res) => {
        return res.triggerPoint;
      })
    ));
  }

  getRecommendations(triggerPointId: string,
    params: RecommendationRequestParams,
    context: string,
    noCaching: boolean = false): Promise<Array<ConnectorPredictions>> {
    let serverUrl = `${this.config.recommendationsUrl}/for/${triggerPointId}`;
    if (noCaching) {
      serverUrl += '?noCaching=true';
    }
    return firstValueFrom(this.authService.securePost<RecommendationRequestParams, TriggerPointRecommendationsResponse>(
      serverUrl, params).pipe(
        map((res) => {
          return res.predictions;
        })
      )).then(predictions => {
        return this._extendArticleRecommendations(predictions, context);
      });
  }

  _selectActiveTriggerPointIds(triggerPointIds: Array<string>): Promise<Array<string>> {
    return this.getTriggerPoints()
      .then(triggerPoints => {
        // select valid trigger points
        const validIds = triggerPoints.map(tp => {
          return tp.name;
        });
        return triggerPointIds.filter(triggerPointId => {
          return validIds.indexOf(triggerPointId) !== -1;
        });
      });
  }

  _loadTriggerPoints(triggerPointIds: Array<string>): Promise<Map<string, TriggerPointInfo>> {
    const promises = triggerPointIds.map(triggerPointId => {
      return this.getTriggerPoint(triggerPointId);
    });
    return Promise.all(promises)
      .then(promiseResults => {
        const res: Map<string, TriggerPointInfo> = new Map();
        promiseResults.forEach(info => {
          res.set(info.name, info);
        });
        return res;
      });
  }

  async _buildRecommendationRequestParams(
    article: ArticleType,
    paramsInfo: Map<string, string>,
    context: RecommendationsContext): Promise<RecommendationRequestParams> {
    const params: RecommendationRequestParams = {};
    for (const paramName in paramsInfo) {
      if (Object.prototype.hasOwnProperty.call(paramsInfo, paramName)) {
        switch (paramName) {
          case 'context':
            params[paramName] = context.context;
            break;
          case 'articleId':
            params[paramName] = article._id;
            break;
          case 'userId':
            params[paramName] = context.userId;
            break;
          case 'skipVisited':
            params[paramName] = true;
            break;
          case 'text':
            let text: string;
            if (article.type === ItemType.article) {
              text = article.content;
            } else if (article.journal === 'youtube.com') {
              if (article.hasTranscript) {
                try {
                  const texts = await this.articleService.getVideoTranscriptSummary(article._id);
                  text = [
                    article.title,
                    ...texts
                  ].join('\n');
                } catch (err) {
                  console.error(err);
                  text = article.title;
                }
              }
              else {
                text = article.title
              }
            }
            params[paramName] = text;
            break;
          default:
            console.error(`not expected paramName: ${paramName}`);
        }
      }
    }
    return params;
  }


  _extendArticleRecommendations(connectorsPredictions: Array<ConnectorPredictions>, context: string): Promise<Array<ConnectorPredictions>> {
    const articleIds = [];
    _.each(connectorsPredictions, connectorPredictions => {
      connectorPredictions.recommendations.forEach(recommendation => {
        if (recommendation.type === RecommendationItemType.article.valueOf()) {
          articleIds.push(recommendation.id);
        }
      });
    });
    if (articleIds.length === 0) {
      return Promise.resolve(connectorsPredictions);
    }
    return this.articleService.getArticlesForContext(articleIds, context).then(articles => {
      const articlesLookup: Map<string, Article> = new Map();
      articles.forEach(article => {
        articlesLookup[article._id] = article;
      });
      _.each(connectorsPredictions, connectorPredictions => {
        connectorPredictions.recommendations.forEach(recommendation => {
          if (recommendation.type === RecommendationItemType.article.valueOf()) {
            recommendation.article = articlesLookup[recommendation.id];
          }
          articleIds.push(recommendation.id)
        });
      });
      return connectorsPredictions;
    });
  }

  _fetchArticles(articleIds: Array<string>): Promise<Array<ArticleType>> {
    return firstValueFrom(this.articleService.getArticles(articleIds).pipe(
      map(articlesResponse => {
        if ((articlesResponse as ArticlesSuccessResponse)?.result?.length !== 1) {
          throw new Error('article is not found'); // TODO: ui indication
        }
        return (articlesResponse as ArticlesSuccessResponse)?.result;
      })
    ));
  }

}
